import {
  Control,
  Controller,
  FieldErrors,
  get,
  useFormContext,
} from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';

import { IcPlus } from '@/assets/images';
import {
  SelectItem as SelectItemType,
  RenderFormField,
  AddressForm,
} from '@/components';
import { FormFilePicker } from '@/components/FormElements/FormFilePicker/FormFilePicker';
import {
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  DateTimePicker,
  MultiselectCombobox,
  Combobox,
  Checkbox,
  Button,
  SignatureCanvas,
} from '@/components/TailwindComponents';
import { DatePicker } from '@/components/TailwindComponents/components/Form/DatePicker/DatePicker';
import { TimePicker } from '@/components/TailwindComponents/components/Form/TimePicker/TimePicker';
import { cn } from '@/components/TailwindComponents/lib/utils';
import { IAddress, IFile } from '@/models';

import { IArrestFormFieldConfig } from '../data';
import { IArrestFormData } from '../types';
import ReadOnlyField from './ReadOnlyField';

interface FormFieldRendererProps {
  field: IArrestFormFieldConfig;
  control: Control<IArrestFormData>;
  errors: FieldErrors<IArrestFormData>;
  viewMode?: boolean;
  getItems?: (itemsKey: string) => SelectItemType[];
  onUseIncidentAddress?: () => void;
  hideInput?: boolean;
  customValue?: any;
  onCustomChange?: (value: any) => void;
  onAddNewCourt?: () => void;
}

// Helper function to render required labels
const renderRequiredLabel = (label: string, required?: boolean) => {
  return (
    <>
      {label}
      {required && <span className="text-red-500 opacity-80"> *</span>}
    </>
  );
};

const FormFieldRenderer = ({
  field,
  control,
  errors,
  viewMode = false,
  getItems,
  onUseIncidentAddress,
  hideInput = false,
  customValue,
  onCustomChange,
  onAddNewCourt,
}: FormFieldRendererProps) => {
  const [searchParams] = useSearchParams();
  const { watch } = useFormContext<IArrestFormData>();

  // Handle conditional rendering - return null if condition not met
  if (field.conditional) {
    const conditionMet =
      watch(field.conditional.field as keyof IArrestFormData) ===
      field.conditional.value;
    if (!conditionMet) {
      return null;
    }
  }

  const labelClassName =
    field.labelClassName ??
    'text-sm font-medium text-gray-700 dark:text-gray-200';

  // Handle read-only type fields (used in ArresteePersonalInfoTab)
  if (field.type === 'readonly') {
    return (
      <Controller
        name={field.name as keyof IArrestFormData}
        control={control}
        render={({ field: { value } }) => (
          <ReadOnlyField
            label={field.label}
            value={value as string | number | null | undefined}
            className={field.className}
          />
        )}
      />
    );
  }

  // Special handling for "Use Incident Address" button (hide in view mode but maintain layout)
  if (field.name === 'useIncidentButton') {
    return (
      <div
        className={cn('flex flex-col gap-0.5', field.className)}
        style={{ visibility: viewMode || hideInput ? 'hidden' : 'visible' }}
      >
        <label className="invisible text-sm font-medium text-gray-700 dark:text-gray-200">
          {renderRequiredLabel(field.label, field.required)}
        </label>
        <Button
          type="button"
          onClick={onUseIncidentAddress}
          variant="secondary"
          size="sm"
          disabled={viewMode}
        >
          {field.label}
        </Button>
      </div>
    );
  }

  const rawItems =
    field.items && typeof field.items === 'string'
      ? getItems?.(field.items) || []
      : (field.items as { label: string; value: string }[]) || [];

  // Ensure all items have string values for compatibility
  const items = rawItems.map((item) => ({
    label: String(item.label),
    value: String(item.value),
  }));

  return (
    <div className={cn('flex flex-col gap-0.5', field.className)}>
      <Controller
        name={field.name as keyof IArrestFormData}
        control={control}
        render={({ field: { onChange, value } }) => {
          // Use custom value and onChange if provided
          const fieldValue = customValue !== undefined ? customValue : value;
          const handleChange = onCustomChange || onChange;
          switch (field.type) {
            case 'text':
              return (
                <div
                  className={`flex flex-col gap-0.5 ${
                    searchParams?.get('fieldKey') === field.name
                      ? 'reportRequired'
                      : ''
                  }`}
                >
                  <label className={labelClassName}>
                    {renderRequiredLabel(field.label, field.required)}
                  </label>
                  <Input
                    type="text"
                    value={(fieldValue as string) || ''}
                    onChange={handleChange}
                    placeholder={field.placeholder}
                    disabled={field.disabled || viewMode}
                    className="w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  />
                </div>
              );

            case 'number':
              return (
                <div
                  className={`flex flex-col gap-0.5 ${
                    searchParams?.get('fieldKey') === field.name
                      ? 'reportRequired'
                      : ''
                  }`}
                >
                  <label className={labelClassName}>
                    {renderRequiredLabel(field.label, field.required)}
                  </label>
                  <Input
                    type="number"
                    value={(fieldValue as string | number) ?? ''}
                    onChange={handleChange}
                    placeholder={field.placeholder}
                    disabled={field.disabled || viewMode}
                    min={field.min}
                    className="w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  />
                </div>
              );

            case 'textarea':
              return (
                <div
                  className={`flex flex-col gap-0.5 ${
                    searchParams?.get('fieldKey') === field.name
                      ? 'reportRequired'
                      : ''
                  }`}
                >
                  <label className={labelClassName}>
                    {renderRequiredLabel(field.label, field.required)}
                  </label>
                  <Textarea
                    value={(value as string) || ''}
                    onChange={handleChange}
                    placeholder={field.placeholder}
                    rows={field.rows || 3}
                    disabled={field.disabled || viewMode}
                    className="w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  />
                </div>
              );

            case 'date':
              return (
                <div
                  className={`flex flex-col gap-0.5 ${
                    searchParams?.get('fieldKey') === field.name
                      ? 'reportRequired'
                      : ''
                  }`}
                >
                  <label className={labelClassName}>
                    {renderRequiredLabel(field.label, field.required)}
                  </label>
                  <DatePicker
                    date={value ? new Date(value as string | Date) : undefined}
                    onSelect={(selectedDate) => {
                      handleChange(selectedDate || null);
                    }}
                    placeholder={field.placeholder || 'MM/DD/YYYY'}
                    disabled={field.disabled || viewMode}
                    clearable={!viewMode}
                  />
                </div>
              );

            case 'time':
              return (
                <div
                  className={`flex flex-col gap-0.5 ${
                    searchParams?.get('fieldKey') === field.name
                      ? 'reportRequired'
                      : ''
                  }`}
                >
                  <label className={labelClassName}>
                    {renderRequiredLabel(field.label, field.required)}
                  </label>
                  <TimePicker
                    time={(value as string) || undefined}
                    onSelect={(selectedTime) => {
                      handleChange(selectedTime || '');
                    }}
                    placeholder={field.placeholder || 'HH:mm'}
                    disabled={field.disabled || viewMode}
                    clearable={!viewMode}
                  />
                </div>
              );

            case 'datetime':
              return (
                <div
                  className={`flex flex-col gap-0.5 ${
                    searchParams?.get('fieldKey') === field.name
                      ? 'reportRequired'
                      : ''
                  }`}
                >
                  <label className={labelClassName}>
                    {renderRequiredLabel(field.label, field.required)}
                  </label>
                  <DateTimePicker
                    date={value ? new Date(value as string | Date) : undefined}
                    onSelect={(selectedDate) => {
                      handleChange(selectedDate || null);
                    }}
                    placeholder={field.placeholder || 'MM/DD/YYYY HH:mm'}
                    disabled={field.disabled || viewMode}
                  />
                </div>
              );

            case 'select':
              return (
                <div
                  className={`flex flex-col gap-0.5 ${
                    searchParams?.get('fieldKey') === field.name
                      ? 'reportRequired'
                      : ''
                  }`}
                >
                  <label className={labelClassName}>
                    {renderRequiredLabel(field.label, field.required)}
                  </label>
                  <Select
                    onValueChange={(selectedValue) => {
                      if (fieldValue === selectedValue) {
                        handleChange(null);
                      } else {
                        handleChange(selectedValue);
                      }
                    }}
                    value={fieldValue ? String(fieldValue) : ''}
                    disabled={field.disabled || viewMode}
                  >
                    <SelectTrigger
                      className="w-full"
                      clearable={!viewMode}
                      hasValue={!!fieldValue}
                      onClear={() => handleChange(null)}
                    >
                      <SelectValue placeholder={field.placeholder} />
                    </SelectTrigger>
                    <SelectContent>
                      {items.map(({ label, value: optionValue }) => (
                        <SelectItem
                          key={optionValue}
                          value={String(optionValue)}
                        >
                          {label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              );

            case 'multiselect':
              return (
                <div
                  className={`flex flex-col gap-0.5 ${
                    searchParams?.get('fieldKey') === field.name
                      ? 'reportRequired'
                      : ''
                  }`}
                >
                  <label className={labelClassName}>
                    {renderRequiredLabel(field.label, field.required)}
                  </label>
                  <MultiselectCombobox
                    options={items}
                    value={(value as string[]) || []}
                    onSelect={handleChange}
                    placeholder={field.placeholder}
                    disabled={field.disabled || viewMode}
                    classNames="w-full"
                  />
                </div>
              );

            case 'autocomplete':
              return (
                <div
                  className={`flex flex-col gap-0.5 ${
                    searchParams?.get('fieldKey') === field.name
                      ? 'reportRequired'
                      : ''
                  }`}
                >
                  <label className={labelClassName}>
                    {renderRequiredLabel(field.label, field.required)}
                  </label>
                  <RenderFormField
                    name={field.name}
                    type="autocomplete"
                    placeholder={field.placeholder}
                  />
                </div>
              );

            case 'address':
              return (
                <div
                  className={`flex flex-col gap-0.5 ${
                    searchParams?.get('fieldKey') === field.name
                      ? 'reportRequired'
                      : ''
                  }`}
                >
                  <AddressForm
                    onChange={handleChange}
                    name={field.name}
                    value={(value as IAddress) ?? null}
                    disabled={field.disabled || viewMode}
                    label={field.label}
                    required={field.required}
                    showMunicipalityTypeInput={field.showMunicipalityTypeInput}
                  />
                </div>
              );

            case 'signature':
              return (
                <div
                  className={`flex flex-col gap-0.5 ${
                    searchParams?.get('fieldKey') === field.name
                      ? 'reportRequired'
                      : ''
                  }`}
                >
                  <SignatureCanvas
                    label={field.label}
                    value={(value as string) || ''}
                    onChange={handleChange}
                    disabled={field.disabled || viewMode}
                  />
                </div>
              );

            case 'checkbox':
              return (
                <div
                  className={`flex w-full space-x-2 ${
                    searchParams?.get('fieldKey') === field.name
                      ? 'reportRequired'
                      : ''
                  }`}
                >
                  <Checkbox
                    id={field.name}
                    checked={!!value}
                    onCheckedChange={handleChange}
                    disabled={field.disabled || viewMode}
                    className="border-red-500 data-[state=checked]:bg-red-500 data-[state=checked]:border-red-500"
                  />
                  <label
                    htmlFor={field.name}
                    className={cn(labelClassName, 'cursor-pointer select-none')}
                  >
                    {renderRequiredLabel(field.label, field.required)}
                  </label>
                </div>
              );

            case 'toggle':
              return (
                <div
                  className={`flex items-center space-x-3 ${
                    searchParams?.get('fieldKey') === field.name
                      ? 'reportRequired'
                      : ''
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => handleChange(!value)}
                    className={cn(
                      'relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2',
                      value ? 'bg-red-500' : 'bg-gray-200 dark:bg-gray-600',
                    )}
                    disabled={field.disabled || viewMode}
                  >
                    <span
                      className={cn(
                        'inline-block h-4 w-4 transform rounded-full bg-white dark:bg-gray-200 transition-transform',
                        value ? 'translate-x-6' : 'translate-x-1',
                      )}
                    />
                  </button>
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                    {field.label}
                  </span>
                </div>
              );

            case 'file':
              return (
                <div className="flex flex-col gap-0.5">
                  <label className={labelClassName}>
                    {renderRequiredLabel(field.label, field.required)}
                  </label>
                  <FormFilePicker
                    name={field.name}
                    initialFileItems={(value as IFile[]) || []}
                    disabled={field.disabled || viewMode}
                    multiple
                    handleChange={(_, files) => handleChange(files)}
                  />
                </div>
              );

            case 'searchableSelect':
              return (
                <div
                  className={`flex flex-col gap-0.5 ${
                    searchParams?.get('fieldKey') === field.name
                      ? 'reportRequired'
                      : ''
                  }`}
                >
                  <span
                    className={cn(
                      'flex w-full items-center justify-between',
                      labelClassName,
                    )}
                  >
                    <label>
                      {renderRequiredLabel(field.label, field.required)}
                    </label>

                    {(field.name === 'arraignmentCourt' ||
                      field.name === 'returnCourt') &&
                      onAddNewCourt &&
                      !viewMode && (
                        <span
                          className="flex text-red-500 hover:text-red-600 cursor-pointer items-center"
                          onClick={onAddNewCourt}
                        >
                          <IcPlus width={14} height={14} />
                          <span className="text-xs font-bold">
                            Add New Court
                          </span>
                        </span>
                      )}
                  </span>

                  <Combobox
                    options={
                      (typeof field.items === 'string'
                        ? getItems?.(field.items) || []
                        : field.items || []
                      ).map((item) => ({
                        value: String(item.value),
                        label: item.label,
                      })) as { value: string; label: string }[]
                    }
                    value={(fieldValue as string) || ''}
                    onSelect={(selectedValue) =>
                      handleChange(selectedValue || null)
                    }
                    placeholder={field.placeholder || 'Select...'}
                    searchPlaceholder="Search..."
                    emptyResultsMessage="No items found"
                    disabled={field.disabled || viewMode}
                    clearable={!viewMode}
                    className="w-full"
                  />
                </div>
              );

            default:
              return (
                <div
                  className={`flex flex-col gap-0.5 ${
                    searchParams?.get('fieldKey') === field.name
                      ? 'reportRequired'
                      : ''
                  }`}
                >
                  <label className={labelClassName}>
                    {renderRequiredLabel(field.label, field.required)}
                  </label>
                  <Input
                    type={field.type}
                    value={(fieldValue as string) || ''}
                    onChange={handleChange}
                    placeholder={field.placeholder}
                    disabled={field.disabled || viewMode}
                    className="w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  />
                </div>
              );
          }
        }}
      />
      {get(errors, field.name) && (
        <div className="text-sm text-red-500">
          {String(get(errors, field.name)?.message)}
        </div>
      )}
    </div>
  );
};

export default FormFieldRenderer;
